/**
 * Shared geometry for the workspace page's regions.
 *
 * One number per measurement, so a skeleton and the real component it stands
 * in for size themselves off the same value — a skeleton that drifts from the
 * geometry it is meant to preview is worse than no skeleton, because it makes
 * the page SHIFT once real content replaces it. Values are taken from the
 * design reference (folder tile, card grid, list rows, sidebar tree, header).
 */

/** Folder/file tile in the grid view: icon box, not counting the two-line name below it. */
export const FOLDER_TILE_GLYPH_WIDTH_PX = 64;
export const FOLDER_TILE_GLYPH_HEIGHT_PX = 56;
/**
 * Narrowest a grid tile may get. The grid fits as many tiles as its CONTAINER
 * allows at this width (auto-fit): 8 on the design's 1072px canvas (about
 * 125px each), so a short name like "readings.csv" fits its line instead of
 * breaking mid-word. The phone grid keeps a fixed three columns of its own.
 */
export const FOLDER_TILE_MIN_WIDTH_PX = 120;
/** Space between grid tiles, both ways (Chakra `2`). */
export const FOLDER_TILE_GAP_PX = 8;
/**
 * Full grid cell, one row of `Main` view, including the 8px gap below the tile.
 * The tile must hold padding 16 + glyph 56 + two 4px gaps + a two-line name
 * (2 x 19.25) + the kind line (18) = 136.5px; at 124 a two-line name pushed
 * the kind line under the next row.
 */
export const FOLDER_TILE_ROW_HEIGHT_PX = 148;

/**
 * Collection tile, the same in the folder and card views: a wide row (glyph
 * left, name and "N items · 3d ago" right) in its own section above the files.
 * At this minimum width the design's 1072px canvas fits three, a wide one four.
 */
export const COLLECTION_TILE_MIN_WIDTH_PX = 260;
export const COLLECTION_TILE_HEIGHT_PX = 64;
/** Space between collection tiles, both ways (Chakra `3`). */
export const COLLECTION_TILE_GAP_PX = 12;
/** A section's label row ("COLLECTIONS 7", "FILES 8"), its space above included. */
export const SECTION_HEADER_HEIGHT_PX = 44;
/** The centered "Show all" / "Show less" row under a long collections section. */
export const COLLECTIONS_TOGGLE_ROW_HEIGHT_PX = 40;

/** Card view: the preview panel above a card's name/meta footer. */
export const CARD_PREVIEW_HEIGHT_PX = 212;
/** Full card cell (preview + footer), one row of the card grid. */
export const CARD_ROW_HEIGHT_PX = 290;
/** Narrowest a card may get; the card grid fits as many as its container allows. */
export const CARD_MIN_WIDTH_PX = 260;
/** Space between cards, both ways (Chakra `4`). */
export const CARD_GAP_PX = 16;

/** List view: one data row, and the column header row above it. */
export const LIST_ROW_HEIGHT_PX = 48;
export const LIST_HEADER_ROW_HEIGHT_PX = 40;

/** Sidebar folder tree: one row (any depth). */
export const TREE_ROW_HEIGHT_PX = 32;

/**
 * The workspace page's side padding (top bar, header, items), one value so
 * every page built on the workspace chrome lines up with the canvas.
 */
export const PAGE_GUTTER_PX = { base: 4, lg: 8 } as const;

/**
 * `ArtifactTrail`'s "in <folder>" location link shrinks 20x faster than the
 * trail beside it, so the folder name gives way before the item's own title.
 * A ratio of 2 was not enough in the browser: shrink is weighted by width, so
 * a long folder name still clipped the title at 1280px.
 */
export const TRAIL_SHRINK = 1;
export const LOCATION_LINK_SHRINK = 20;

/** Top bar: the breadcrumb + search + actions row. */
export const BREADCRUMB_ROW_HEIGHT_PX = 56;
/** Canvas header: the title + item-count block beneath the breadcrumb row. */
export const HEADER_META_HEIGHT_PX = 82;
/** Canvas header: the scope, filter, sort and view toolbar row under the title block. */
export const CANVAS_TOOLBAR_HEIGHT_PX = 52;
/**
 * The whole canvas header (title block + toolbar). Declared, never measured
 * off content, so applying a filter (an active pill, Clear filters, a custom
 * range picker) can never push the items below it down.
 */
export const CANVAS_HEADER_HEIGHT_PX = HEADER_META_HEIGHT_PX + CANVAS_TOOLBAR_HEIGHT_PX;

/** The top bar's full search width: room for "Search" and ⌘K, no wider. */
export const SEARCH_BAR_PX = 240;
